import { devLogger } from "@certa/common/utils/devLogger";
import { getInstances } from "../renderingEngine/renderer";
import { getLineInstances } from "../renderingEngine/lineRenderer";
import { spatialRegistry } from "./spatialRegistry";

type CullProps = {
  force?: boolean;
  visibleBounds: { x: number; y: number; width: number; height: number };
};

const cullContext: {
  lastSectors: Set<string>;
  lastVisibleBounds: CullProps["visibleBounds"] | null;
  lastShown: Set<string>;
  lastHidden: Set<string>;
  lastAllSectors: Set<string>;
} = {
  lastSectors: new Set(),
  lastVisibleBounds: null,
  lastShown: new Set(),
  lastHidden: new Set(),
  lastAllSectors: new Set()
};

/**
 * Cull the components that are not visible. The logic is as follows:
 * 1. Convert the visible bounds into sectors
 * 2. Find the intersection of the visible sectors and the last visible sectors
 * 3. Hide the nodes that are in the last visible sectors but not in the intersection
 * 4. Show the nodes that are in the intersection but not in the last visible sectors
 *
 * We allow the cull to be forced, such that it recalculates the culling
 * regardless of the last visible bounds. This is useful when triggering culling
 * from the layout observer when stuff has moved (collapsed/expanded, or just appeared)
 * and in such case the visible bounds may not have changed.
 */
export const cull = ({ force = false, visibleBounds }: CullProps) => {
  const { lastVisibleBounds } = cullContext;

  if (
    !force &&
    lastVisibleBounds &&
    lastVisibleBounds.x === visibleBounds.x &&
    lastVisibleBounds.y === visibleBounds.y &&
    lastVisibleBounds.width === visibleBounds.width &&
    lastVisibleBounds.height === visibleBounds.height
  ) {
    // No change in visible bounds
    return;
  }

  if (force) {
    cullContext.lastSectors.clear();
    cullContext.lastShown.clear();
    cullContext.lastHidden.clear();
    cullContext.lastVisibleBounds = null;
    cullContext.lastAllSectors.clear();
  }

  const t1 = performance.now();
  // Store the last visible bounds to avoid recalculating culling
  cullContext.lastVisibleBounds = visibleBounds;
  const allSectors = spatialRegistry.getAllSectors();
  const newSectors = new Set<string>(); // sectors that are newly added to spatial registry
  const visibleSectors = spatialRegistry.layoutToSectors(visibleBounds);

  // Calculate new sectors
  allSectors.forEach(sector => {
    if (
      !visibleSectors.has(sector) &&
      !cullContext.lastAllSectors.has(sector)
    ) {
      newSectors.add(sector);
    }
  });

  // Update lastAllSectors for the next iteration
  cullContext.lastAllSectors = new Set(allSectors);

  // This will hold the sectors that are visible for the current
  // visible bounds

  const dirtySectors = spatialRegistry.getDirtySectors({
    clearDirtySectors: true
  });
  const sectorsEnteredViewport = new Set<string>(dirtySectors);
  const sectorsExitedViewport = new Set<string>([]);
  const sectorsStillInViewport = new Set<string>([]);

  visibleSectors.forEach(sector => {
    if (!cullContext.lastSectors.has(sector)) {
      sectorsEnteredViewport.add(sector);
    } else {
      sectorsStillInViewport.add(sector);
    }
  });

  cullContext.lastSectors.forEach(sector => {
    if (!visibleSectors.has(sector)) {
      sectorsExitedViewport.add(sector);
    }
  });

  newSectors.forEach(sector => {
    if (!visibleSectors.has(sector)) {
      sectorsExitedViewport.add(sector);
    }
  });

  const keysEnteredIntoViewport = spatialRegistry.retrieveFromSectors(
    sectorsEnteredViewport
  );
  const keysMovedOutOfViewport = spatialRegistry.retrieveFromSectors(
    sectorsExitedViewport
  );

  const keysStillInViewport = spatialRegistry.retrieveFromSectors(
    sectorsStillInViewport
  );
  // This will hold the nodes that are to be shown and hidden
  const keysToShow = new Set<string>();
  const keysToHide = new Set<string>();

  const stillShowing = new Set<string>();
  const stillHiding = new Set<string>();

  const instances = getInstances();
  const lineInstances = getLineInstances();
  keysEnteredIntoViewport.forEach(key => {
    if (cullContext.lastShown.has(key)) {
      stillShowing.add(key);
      return;
    }
    if (instances[key] || lineInstances[key]) {
      // Edge case where this culling update gets triggered between
      // renderer and layoutObserver. That means that some instances may be
      // removed, but haven't been updated in the spatial registry yet, as
      // that happens in layoutObserver.
      // Mostly noticeable in Drop indicator
      keysToShow.add(key);
    }
  });
  keysMovedOutOfViewport.forEach(key => {
    if (cullContext.lastHidden.has(key)) {
      stillHiding.add(key);
      return;
    }
    if (!keysEnteredIntoViewport.has(key) && !keysStillInViewport.has(key)) {
      keysToHide.add(key);
    }
  });

  // Finally we have the list of nodes that are to be shown and hidden
  // However these are sector based and it's possible that some of the nodes
  // are spanning across multiple sectors
  // Therefore, we will iterate over the nodes that need to be shown
  // And if they're also added to the nodesToHide set, we'll remove them from
  // that set.
  keysToShow.forEach(key => {
    const instance = instances[key] ?? lineInstances[key];
    if (instance) {
      if (instance.setCulled(false)) {
        instance.render();
      }
    }
  });

  // Hide the nodes that are not visible
  keysToHide.forEach(key => {
    const instance = instances[key] ?? lineInstances[key];
    if (instance) {
      if (instance.setCulled(true)) {
        instance.render();
      }
    }
  });

  cullContext.lastSectors = new Set(visibleSectors);
  cullContext.lastShown = new Set([...stillShowing, ...keysToShow]);
  cullContext.lastHidden = new Set([...stillHiding, ...keysToHide]);
  cullContext.lastAllSectors = new Set(allSectors);

  const t2 = performance.now();
  devLogger.log(
    `culled in ${(t2 - t1).toFixed(2)}ms with shown: ${keysToShow.size}/${keysEnteredIntoViewport.size} hidden: ${keysToHide.size}/${keysMovedOutOfViewport.size} unchanged: ${keysStillInViewport.size}`
  );
};
